import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PrintIcon from '@mui/icons-material/Print';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { DEAL_STAGE, Lead } from '@tassomutuo-backoffice/types';
import { CtaButton } from '@tassomutuo-backoffice/ui';
import useLeadActionRequired from '../hooks/useLeadActionRequired';
import useDateFormat from '../../../hooks/useDateFormat';
import LabelValueItem from './LabelValueItem';
import { needUpdateNotify } from '../../../services/api/lead/lead';
import { getUserRoleInLocalStorage } from '../../../services/localStorage/localStorage';

interface IProps {
  lead: Lead;
  onUpdateStatusPress: () => void;
  onShowNotesPress: () => void;
  onDropPress: () => void;
  onSearchingPress: () => void;
  onDevelopPress: () => void;
  onAddTaskPress: () => void;
}

const LeadDetailsSummaryInfoBox = ({
  lead,
  onUpdateStatusPress,
  onShowNotesPress,
  onDropPress,
  onSearchingPress,
  onDevelopPress,
  onAddTaskPress,
}: IProps) => {
  const { renderActionRequired } = useLeadActionRequired();
  const { formatDate } = useDateFormat();

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (getUserRoleInLocalStorage() === 'admin') {
      setIsAdmin(true);
    }
  }, []);

  const renderAction = renderActionRequired({ lead });

  const leadProperties = lead.properties;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
          m: 2,
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap-reverse',
              gap: 2,
              borderBottom: (th) => `1px solid ${th.palette.divider}`,
              width: '100%',
              pb: 1,
            }}
          >
            <Typography
              fontWeight="bold"
              textTransform="uppercase"
              sx={{ color: (th) => th.palette.secondary.dark }}
            >
              {leadProperties.readable_dealstage.label}
            </Typography>
            {renderAction}
            <div className="no-print" style={{ display: 'flex', gap: '1rem' }}>
              {isAdmin ? (
                <CtaButton
                  sx={{
                    bgcolor: (th) => th.palette.primary.dark,
                    width: { sm: '100%', xs: '100%' },
                    maxWidth: '140px',
                  }}
                  variant="contained"
                  onClick={() => needUpdateNotify(lead)}
                >
                  <MailOutlineIcon />
                  <Typography fontWeight="bold" pl={1}>
                    Notifica
                  </Typography>
                </CtaButton>
              ) : null}
              <CtaButton
                sx={{
                  bgcolor: (th) => th.palette.primary.dark,
                  width: { sm: '100%', xs: '100%' },
                  maxWidth: '140px',
                }}
                variant="contained"
                onClick={() => window.print()}
              >
                <PrintIcon />
                <Typography fontWeight="bold" pl={1}>
                  Stampa
                </Typography>
              </CtaButton>
              <CtaButton
                sx={{
                  bgcolor: (th) => th.palette.primary.dark,
                  width: { sm: '100%', xs: '100%' },
                  maxWidth: '160px',
                }}
                variant="contained"
                onClick={onAddTaskPress}
              >
                <AddTaskIcon />
                <Typography fontWeight="bold" pl={1}>
                  Promemoria
                </Typography>
              </CtaButton>
            </div>
          </Box>

          <Box pt={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <Box>
                <Typography
                  sx={{ color: (th) => th.palette.secondary.dark }}
                  variant="h5"
                  fontWeight="bold"
                >
                  {leadProperties.dealname}
                </Typography>
                <Typography variant="h6">
                  {leadProperties.phone_number}
                </Typography>
              </Box>
              <LabelValueItem
                containerSx={{ justifyContent: 'flex-end' }}
                valueSx={{ textTransform: 'lowercase' }}
                value={leadProperties.email || ''}
              />
              <LabelValueItem
                containerSx={{ alignItems: { sm: 'flex-end' } }}
                label="Ultima modifica"
                value={
                  leadProperties.notes_last_updated
                    ? formatDate(
                        leadProperties.notes_last_updated,
                        'dd/MM/yyyy HH:mm'
                      )
                    : 'N/A'
                }
              />
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap-reverse"
              gap={2}
              mt={2}
            >
              <LabelValueItem
                label="Responsabile commerciale"
                value={
                  leadProperties.sales_manager_user
                    ? `${leadProperties.sales_manager_user.name} (${leadProperties.sales_manager_user.email})`
                    : 'N/A'
                }
              />

              <LabelValueItem
                containerSx={{ alignItems: { sm: 'flex-end' } }}
                label="Data creazione"
                value={
                  leadProperties.createdate
                    ? formatDate(leadProperties.createdate, 'dd/MM/yyyy HH:mm')
                    : 'N/A'
                }
              />
            </Box>
          </Box>
        </Box>
        <LabelValueItem
          containerSx={{ maxWidth: '800px', my: 2 }}
          label="Note a consulente"
          value={leadProperties.notes}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            mt: 1,
            width: '100%',
            gap: 2,
          }}
          className="no-print"
        >
          <CtaButton
            onClick={onShowNotesPress}
            sx={{ width: { sm: 'auto', xs: '100%' } }}
          >
            <Typography variant="h6" fontWeight="bold" p={1}>
              Consulta le note
            </Typography>
          </CtaButton>
          {leadProperties.phone_number && (
            <Box
              component="a"
              href={`tel:${leadProperties.phone_number}`}
              sx={{ width: { sm: 'auto', xs: '100%' } }}
            >
              <CtaButton
                variant="contained"
                sx={{ width: { sm: 'auto', xs: '100%' } }}
              >
                <PhoneIcon />
                <Typography variant="h6" fontWeight="bold" p={1}>
                  Chiama
                </Typography>
              </CtaButton>
            </Box>
          )}
          {leadProperties.dealstage !== DEAL_STAGE.ATTO_NOTARILE_CONCLUSO && (
            <Box
              sx={{
                width: { sm: '100%', xs: '100%' },
                maxWidth: '400px',
                alignSelf: 'flex-end',
              }}
            >
              <CtaButton
                sx={{
                  bgcolor: (th) => th.palette.error.main,
                  width: { sm: '100%', xs: '100%' },
                  maxWidth: '400px',
                }}
                variant="contained"
                onClick={onUpdateStatusPress}
              >
                <Typography variant="h6" fontWeight="bold" p={1}>
                  Fornisci aggiornamento
                </Typography>
              </CtaButton>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
          bgcolor: 'white',
          m: 2,
          p: 2,
        }}
      >
        {(leadProperties.dealstage === DEAL_STAGE.IN_SVILUPPO ||
          leadProperties.dealstage === DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO ||
          leadProperties.dealstage === DEAL_STAGE.IN_ATTESA_DI_DELIBERA ||
          leadProperties.dealstage === DEAL_STAGE.DELIBERATA) && (
          <Box
            sx={{
              width: { sm: '100%', xs: '100%' },
              maxWidth: '400px',
              alignSelf: 'flex-end',
            }}
          >
            <CtaButton
              sx={{
                // bgcolor: (th) => th.palette.success.main,
                width: { sm: '100%', xs: '100%' },
                maxWidth: '400px',
              }}
              color="success"
              variant="outlined"
              onClick={onSearchingPress}
            >
              <Typography variant="h6" fontWeight="bold" p={1}>
                Sta cercando immobile
              </Typography>
            </CtaButton>
          </Box>
        )}
        {(leadProperties.dealstage === DEAL_STAGE.CONTATTARE_CLIENTE ||
          leadProperties.dealstage === DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE ||
          leadProperties.dealstage === DEAL_STAGE.APPUNTAMENTO_CONFERMATO ||
          leadProperties.dealstage ===
            DEAL_STAGE.APPUNTAMENTO_ANNULLATO_STB_15GG ||
          leadProperties.dealstage === DEAL_STAGE.DROPPED ||
          leadProperties.dealstage ===
            DEAL_STAGE.APPUNTAMENTO_DA_RIFISSARE) && (
          <Box
            sx={{
              width: { sm: '100%', xs: '100%' },
              maxWidth: '400px',
              alignSelf: 'flex-end',
            }}
            className="no-print"
          >
            <CtaButton
              sx={{
                // bgcolor: (th) => th.palette.success.main,
                width: { sm: '100%', xs: '100%' },
                maxWidth: '400px',
              }}
              color="success"
              variant="outlined"
              onClick={onDevelopPress}
            >
              <Typography variant="h6" fontWeight="bold" p={1}>
                Cliente incontrato e fattibile
              </Typography>
            </CtaButton>
          </Box>
        )}
        {leadProperties.dealstage !== DEAL_STAGE.ATTO_NOTARILE_CONCLUSO && (
          <Box
            sx={{
              width: { sm: '100%', xs: '100%' },
              maxWidth: '400px',
              alignSelf: 'flex-end',
            }}
            className="no-print"
          >
            <CtaButton
              sx={{
                // bgcolor: (th) => th.palette.error.main,
                width: { sm: '100%', xs: '100%' },
                maxWidth: '400px',
              }}
              variant="outlined"
              color="error"
              onClick={onDropPress}
            >
              <Typography variant="h6" fontWeight="bold" p={1}>
                Droppa la lead
              </Typography>
            </CtaButton>
          </Box>
        )}
      </Box>
    </>
  );
};

export default LeadDetailsSummaryInfoBox;
